<template>
  <div>
    <main
      :data-component="$options.name"
      class="el-core _fixed"
    >
      <test-account-status-alert v-if="isTestAccount" />

      <app-core-header-communication
        v-if="showCommunication"
        fixed
      />

      <div
        class="el-core__wrapper wrapper"
        :class="{
          _has_communication: showCommunication,
          'wrapper-comunication': showCommunication
        }"
      >
        <div class="el-main">
          <div
            id="core-sidebar"
            data-onboarding="realTime"
            class="el-main__navigation sidemenu-container"
            :class="{
              _open: appMenuOpen,
              _hasAccountStatusAlert: isTestAccount
            }"
          >
            <app-core-header
              class="menu-wrapper"
              :class="{ _has_communication: showCommunication }"
              :drawerOpen="drawerMenuOpen"
              :isOpen="appMenuOpen"
              @appMenuClicked="toggleAppMenu"
              @drawerMenuClicked="toggleDrawerMenu"
              @openOnboarding="openOnboarding"
            ></app-core-header>
            <app-sidemenu
              @appMenuClicked="toggleAppMenu"
              :isOpen="appMenuOpen"
            />
            <div class="sidemenu-footer">
              <app-core-footer />
            </div>
          </div>
          <div class="flex flex-col w-full">
            <app-navigation-bar />
            <div
              id="core-main"
              class="el-main__content content-wrapper !min-h-full"
            >
              <div
                v-if="mustShowGreeting"
                class="el-section"
              >
                <greeting
                  @close="closeGreeting"
                  @explore="openOnboarding"
                />
              </div>

              <dash-timeline-activation
                v-if="mustShowTimelineActivation"
                :showGreeting="showGreeting"
                @closeGreeting="closeGreeting"
              />

              <app-breadcrumb />
              <section
                v-if="showNps"
                class="el-section"
              >
                <nps @close="closeNps" />
              </section>

              <transition
                :name="transitionName"
                mode="out-in"
              >
                <dash-platform-setup>
                  <help-center
                    :class="{ hidden: !showDrawerHelpCenter }"
                    v-show="showDrawerHelpCenter"
                    :open="showDrawerHelpCenter"
                    :ref="handleHelperCenterRef"
                  />
                  <fees-calculator
                    :class="{ hidden: !showDrawerFeesCalculator }"
                    v-show="showDrawerFeesCalculator"
                    :open="showDrawerFeesCalculator"
                    :ref="handleFeesCalculatorRef"
                  />
                </dash-platform-setup>
              </transition>

              <AppFooter />
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import '@pagarme/help-center/web-comp'
import '@pagarme/fees-calculator/web-comp'

import { mapActions, mapGetters } from 'vuex'

import { isActiveAccount } from '@/enums/account-status.enum'
import { isPsP } from '@/enums/business-model.enum.js'
import { isOwner } from '@/enums/merchant-type.enum'

export default {
  name: 'LayoutCore',
  components: {
    AppNavigationBar: () =>
      import('@/components/project/ui/AppNavigationBar/index.vue'),
    AppBreadcrumb: () =>
      import('@/components/project/ui/AppBreadcrumb/index.vue'),
    AppFooter: () => import('@/components/project/globals/Footer.vue'),
    DashPlatformSetup: () =>
      import('@/components/dash-platform/DashPlatformSetup.vue'),
    AppCoreHeaderCommunication: () =>
      import(
        /* webpackChunkName: "AppCoreHeaderCommunication" */ '@/components/project/ui/menu/AppCore/AppCoreHeaderCommunication.vue'
      ),
    AppCoreHeader: () =>
      import(
        /* webpackChunkName: "AppCoreHeader" */ '@/components/project/ui/menu/AppCore/AppCoreHeader.vue'
      ),
    AppSidemenu: () =>
      import(
        /* webpackChunkName: "AppSidemenu" */ '@/components/project/ui/menu/AppSidemenu.vue'
      ),
    AppCoreFooter: () =>
      import(
        /* webpackChunkName: "AppCoreFooter" */ '@/components/project/ui/menu/AppCore/AppCoreFooter.vue'
      ),
    Greeting: () =>
      import(
        /* webpackChunkName: "Greeting" */ '@/components/project/globals/Greeting.vue'
      ),
    TestAccountStatusAlert: () =>
      import(
        /* webpackChunkName: "TestAccountStatusAlert" */ '@/components/project/globals/AccountsStatusAlert/TestAccountStatusAlert.vue'
      ),
    Nps: () =>
      import(
        /* webpackChunkName: "Nps" */ '@/components/project/globals/Nps.vue'
      ),
    DashTimelineActivation: () =>
      import(
        /* webpackChunkName: "DashTimelineActivation" */ '@/components/project/globals/DashTimelineActivation/index.vue'
      )
  },
  data () {
    return {
      appMenuOpen: false,
      drawerMenuOpen: false,
      transitionName: 'slide-right',
      enableTimelineActivation: false
    }
  },
  watch: {
    $route (to, from) {
      const toDepth = to.path.split('/').length
      const fromDepth = from.path.split('/').length
      this.transitionName = toDepth < fromDepth ? 'slide-right' : 'slide-left'
      // closes appMenu on route change
      if (to !== from && this.appMenuOpen) this.appMenuOpen = false
      this.checkRouteEnabled()
    }
  },
  computed: {
    ...mapGetters([
      'onboarding',
      'isEmployee',
      'isTestAccount',
      'showNps',
      'settings',
      'account',
      'businessModel',
      'merchant',
      'merchantId',
      'accountId',
      'isCheckoutV2Enabled',
      'GET_DRAWER_HELP_CENTER',
      'GET_DRAWER_FEES_CALCULATOR'
    ]),
    showDrawerHelpCenter () {
      return this.GET_DRAWER_HELP_CENTER
    },
    showDrawerFeesCalculator () {
      return this.GET_DRAWER_FEES_CALCULATOR
    },
    isMundiTheme () {
      return ['mundipagg'].includes(process.env.VUE_APP_THEME)
    },
    showCommunication () {
      return this.isMundiTheme
    },
    showGreeting () {
      return this.onboarding.show_greeting && !this.isEmployee
    },
    routeName () {
      return this.$route.name
    },
    showTimelineActivation () {
      const dateStartShowTimeline = new Date(2024, 4, 13)
      const isNewAccount =
        new Date(this.settings?.created_at) >= dateStartShowTimeline

      return (
        isNewAccount &&
        !this.isTestAccount &&
        isActiveAccount(this.account?.status.key) &&
        isPsP(this.businessModel) &&
        isOwner(this.merchant.type.key) &&
        !this.isCheckoutV2Enabled
      )
    },
    mustShowGreeting () {
      return this.showGreeting && !this.mustShowTimelineActivation
    },
    mustShowTimelineActivation () {
      return this.enableTimelineActivation && this.showTimelineActivation
    }
  },
  created () {
    this.checkRouteEnabled()
  },
  beforeDestroy () {
    this.$refs.handleHelperCenterRef &&
      this.$refs.handleHelperCenterRef.removeEventListener('onToggleHelpCenter')
    this.$refs.handleFeesCalculatorRef &&
      this.$refs.handleFeesCalculatorRef.removeEventListener(
        'onToggleHelpCenter'
      )
  },
  methods: {
    ...mapActions([
      'CLOSE_GREETING',
      'TOGGLE_ONBOARDING',
      'UPDATE_ONBOARDING_STEP',
      'TOGGLE_NPS',
      'SET_DRAWER_HELP_CENTER',
      'SET_DRAWER_FEES_CALCULATOR'
    ]),
    handleHelperCenterRef (ref) {
      ref &&
        ref.addEventListener('onToggleHelpCenter', ref => {
          this.SET_DRAWER_HELP_CENTER(ref.detail?.toggle ?? false)
        })
    },
    handleFeesCalculatorRef (ref) {
      ref &&
        ref.addEventListener('onToggleHelpCenter', ref => {
          this.SET_DRAWER_FEES_CALCULATOR(ref.detail?.toggle ?? false)
        })
    },
    toggleAppMenu () {
      this.appMenuOpen = !this.appMenuOpen
      if (this.drawerMenuOpen) this.drawerMenuOpen = false
    },
    toggleDrawerMenu () {
      this.drawerMenuOpen = !this.drawerMenuOpen
      if (this.appMenuOpen) this.appMenuOpen = false
    },
    openOnboarding () {
      if (this.showGreeting) {
        this.CLOSE_GREETING()
      }
      this.TOGGLE_ONBOARDING(true)
    },
    openOnboardingIndication () {
      this.UPDATE_ONBOARDING_STEP(6)
      this.TOGGLE_ONBOARDING(true)
    },
    closeGreeting () {
      this.CLOSE_GREETING()
      this.openOnboardingIndication()
    },
    closeNps () {
      this.TOGGLE_NPS()
    },
    checkRouteEnabled () {
      const routesForShowTimeline = ['home', 'orders', 'charges']
      this.enableTimelineActivation = routesForShowTimeline.includes(
        this.routeName
      )
    }
  }
}
</script>
<style>
.el-main__content .jade-container {
  padding: 0 !important;
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition-duration: 500ms;
  transition-property: opacity, transform;
  transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
  overflow: hidden;
}

.slide-left-enter,
.slide-right-leave-active {
  opacity: 0;
  transform: translateX(calc(2em - 6px));
}

.slide-left-leave-active,
.slide-right-enter {
  opacity: 0;
  transform: translateX(calc(-2em - 6px));
}

.sidemenu-container {
  z-index: 1000;
  margin-top: 0 !important;
  height: 100vh !important;
  min-width: 248px !important;
  width: 248px !important;
}

.sidemenu-container._hasAccountStatusAlert {
  height: calc(100vh - 44px) !important;
}

.wrapper {
  margin-top: 0 !important;
}

.wrapper.wrapper-comunication {
  margin-top: 39px !important;
}

.content-wrapper {
  padding-left: 272px !important;
}

@media only screen and (min-width: 543px) {
  .menu-wrapper._has_communication {
    margin-top: 0 !important;
  }
}

@media only screen and (max-width: 543px) {
  .menu-wrapper._has_communication {
    margin-top: 11px !important;
  }
}

@media only screen and (max-width: 768px) {
  .sidemenu-footer {
    display: none;
  }
  .sidemenu-container {
    height: 0 !important;
    width: 100% !important;
  }
  .sidemenu-container._open {
    height: 100vh !important;
  }
  .sidemenu-container._hasAccountStatusAlert {
    height: calc(100vh - 44px) !important;
  }
  .content-wrapper {
    padding: 0 8px !important;
  }
}
</style>
